import moment from "moment-timezone";
import {
  ICreateLicense,
  LICENSE_STATUSES,
  IWorkerLicenseChangeLog,
  IUpdateLicense,
} from "./interfaces";
import { LICENSE_ACTIONS, NON_LICENSE_ACTIVE } from "./constants";
import { uniq } from "lodash";
import { isDefined } from "@clipboard-health/util-ts";
import { isEmpty } from "lodash";

export const getAgentQualificationsForState = ({
  facilityState,
  agentLicensesData,
  nlcStatesObj,
  firstOptionPreference,
}: {
  facilityState?: string;
  agentLicensesData: Array<{
    state?: string;
    multiState?: boolean;
    qualification: string;
  }>;
  nlcStatesObj: { active_states: string[] };
  firstOptionPreference?: string;
}): string[] => {
  const qualifications: string[] = [];

  const nlcStatesArr = nlcStatesObj?.active_states ?? [];
  for (const license of agentLicensesData) {
    if (
      facilityState === license.state ||
      (license.state ?? "").toLowerCase() === "any" ||
      (license.multiState && [...nlcStatesArr, license.state].includes(facilityState))
    ) {
      qualifications.push(license.qualification);
    }
  }
  if (firstOptionPreference && qualifications.includes(firstOptionPreference)) {
    const qualificationsWithoutPreferenceOne = qualifications.filter(
      (qual) => qual !== firstOptionPreference
    );
    return uniq([firstOptionPreference, ...qualificationsWithoutPreferenceOne.sort()]);
  }
  return uniq(qualifications.sort());
};
export const getUniqueQualifications = (
  licenses?: Array<{ qualification: string }>,
  firstOptionPreference = ""
): string[] => {
  if (licenses?.length === 0 || !licenses) {
    return [NON_LICENSE_ACTIVE];
  }
  const qualifications: string[] = licenses.map((license) => license.qualification);

  if (firstOptionPreference && qualifications.includes(firstOptionPreference)) {
    const qualificationsWithoutPreferenceOne = qualifications.filter(
      (qual) => qual !== firstOptionPreference
    );
    return uniq([firstOptionPreference, ...qualificationsWithoutPreferenceOne.sort()]);
  }
  return uniq(qualifications.sort());
};

export const truncateQualifications = (qualifications: string[], truncateNumber = 3): string => {
  const shouldTruncate = qualifications.slice(truncateNumber - 1).length > 0;
  const formattedQualifications = [
    ...qualifications.slice(0, truncateNumber - 1),
    shouldTruncate ? `+ ${qualifications.slice(truncateNumber - 1).length} more` : "",
  ].join(", ");

  if (!shouldTruncate) {
    return formattedQualifications.slice(0, -2);
  }
  return formattedQualifications;
};

export function formatLicenseDates(license: IUpdateLicense, workerTmz: string) {
  const adjustedExpiryDate =
    isDefined(license.expiresAt) && !isEmpty(license.expiresAt)
      ? moment.tz(license.expiresAt, workerTmz)
      : undefined;
  const adjustedIssueDate =
    isDefined(license.issueDate) && !isEmpty(license.issueDate)
      ? moment.utc(license.issueDate)
      : undefined;

  return {
    ...license,
    ...(isDefined(adjustedExpiryDate) && { expiresAt: adjustedExpiryDate.endOf("day").format() }),
    ...(isDefined(adjustedIssueDate) && { issueDate: adjustedIssueDate.format() }),
  };
}

export function getLicenseValidationState(
  license: Pick<ICreateLicense, "status" | "expiresAt" | "state" | "qualification">,
  autoApprovedQualifications: Set<string>
): {
  isExpirationRequiredForActiveStatus: boolean;
  isAnyStateInvalidForClinicalLicense: boolean;
} {
  return {
    isExpirationRequiredForActiveStatus:
      license.status === LICENSE_STATUSES.ACTIVE && !license.expiresAt,
    isAnyStateInvalidForClinicalLicense:
      Boolean(license.qualification) &&
      !autoApprovedQualifications.has(license.qualification) &&
      license.state === "Any",
  };
}

export const getConfirmationModalText = (
  license: ICreateLicense,
  statesInMultiState = 0
): string => {
  let state = "";
  if (license.multiState) {
    state = `${statesInMultiState} states`;
  } else {
    state = license.state;
  }

  let stateWithPrefix = ` in ${state}`;

  if (license.state === "Any") {
    stateWithPrefix = "";
  }

  if (license.status === LICENSE_STATUSES.ACTIVE) {
    return `This HCP will be able to book shifts${stateWithPrefix} immediately.`;
  }

  return `This may require un-assignment of future shifts the HCP has booked${stateWithPrefix}, and prevent them from booking more until a new license is Active.`;
};

export const getFormattedLog = (changeLog: IWorkerLicenseChangeLog): JSX.Element => {
  const blank = "(blank)";
  const { performedBy, updatedFields, document, createdAt, action } = changeLog;
  const createdDate = moment(createdAt).format("YYYY-MM-DD HH:mm a");
  const {
    userId: performedByUserId,
    type,
    email: performedByEmail,
  } = performedBy || {
    userId: "System (auto)",
    type: "System",
    email: "System",
  };
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let updatedFieldsJson: Array<Record<string, any>> = JSON.parse(updatedFields);
  // will remove below hack once the logs data is cleaned on LM
  if (!Array.isArray(updatedFieldsJson)) {
    updatedFieldsJson = [updatedFieldsJson];
  }

  return (
    <p>
      {createdDate} - {performedByEmail || performedByUserId}({type}) {action} license document{" "}
      {document._id}'s{" "}
      {updatedFieldsJson.map((fields) => {
        const { key } = fields;
        let { oldValue, newValue } = fields;
        if (typeof newValue === "boolean") {
          newValue = `${newValue}`;
        }
        if (typeof oldValue === "boolean") {
          oldValue = `${oldValue}`;
        }
        if (key === "expiresAt") {
          newValue = newValue ? moment(newValue).format("DD MMM YYYY") : blank;
          oldValue = oldValue ? moment(oldValue).format("DD MMM YYYY") : blank;
        }
        return (
          <p key={key}>
            {action === LICENSE_ACTIONS.UPDATED && (
              <>
                {key} from <i>{oldValue || blank}</i> to <b>{newValue || blank}</b>
              </>
            )}
            {action === LICENSE_ACTIONS.ADDED && (
              <>
                {key} <b>{newValue || blank}</b>
              </>
            )}
          </p>
        );
      })}
    </p>
  );
};
